import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import SafeIcon from '../../common/SafeIcon';
import Layout from '../Layout';
import * as FiIcons from 'react-icons/fi';

const { FiHelpCircle, FiInfo, FiMusic, FiUser, FiAward, FiSettings } = FiIcons;

const HelpPage = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();

  // Direct inline style for title color
  const titleStyle = {
    fontWeight: 700,
    color: isDark ? '#0ea5e9' : '#6b21a8' // sky-500 for dark, purple-800 for light
  };

  return (
    <Layout>
      <div className="space-y-6">
        <Helmet>
          <title>{language === 'zh' ? '使用幫助 | Beat Buddy 節拍器' : 'Help | Beat Buddy Metronome'}</title>
          <meta name="description" content={language === 'zh' ? "獲取 Beat Buddy 節拍器的使用指南、常見問題解答和疑難排解。了解如何充分利用這款互動式音樂節拍器應用。" : "Get usage guides, FAQs and troubleshooting for Beat Buddy metronome. Learn how to make the most of this interactive music metronome app."} />
          <meta name="keywords" content="Beat Buddy, metronome guide, how to use metronome, FAQs, music practice help, rhythm training" />
          <link rel="canonical" href="https://beatbuddy.app/help" />
        </Helmet>

        <div className="text-center max-w-xl mx-auto">
          <h2 className="text-3xl mb-2">
            <span style={titleStyle}>{t('helpTitle')}</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mx-auto">
            {t('helpSubtitle')}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
          <div className="flex items-center mb-6">
            <SafeIcon icon={FiHelpCircle} className="w-6 h-6 text-purple-600 dark:text-purple-400 mr-2" />
            <h3 className="text-xl font-semibold text-purple-600 dark:text-purple-400">{t('commonQuestions')}</h3>
          </div>

          <div className="space-y-6">
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">{t('howToStart')}</h4>
              <p className="text-gray-600 dark:text-gray-300">
                {t('howToStartAnswer')}
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">{t('howToUnlock')}</h4>
              <p className="text-gray-600 dark:text-gray-300">
                {t('howToUnlockAnswer')}
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">{t('howToSetAccent')}</h4>
              <p className="text-gray-600 dark:text-gray-300">
                {t('howToSetAccentAnswer')}
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">{t('noSoundIssue')}</h4>
              <p className="text-gray-600 dark:text-gray-300">
                {t('noSoundAnswer')}
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">{t('dataSaving')}</h4>
              <p className="text-gray-600 dark:text-gray-300">
                {t('dataSavingAnswer')}
              </p>
            </div>
          </div>

          <div className="mt-10">
            <div className="flex items-center mb-6">
              <SafeIcon icon={FiInfo} className="w-6 h-6 text-purple-600 dark:text-purple-400 mr-2" />
              <h3 className="text-xl font-semibold text-purple-600 dark:text-purple-400">{t('featureGuide')}</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                <div className="flex items-center mb-3">
                  <SafeIcon icon={FiMusic} className="w-5 h-5 text-purple-500 dark:text-purple-400 mr-2" />
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200">{t('metronomeFeatures')}</h4>
                </div>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li>• <strong>{t('metronomeFeature1')}</strong></li>
                  <li>• <strong>{t('metronomeFeature2')}</strong></li>
                  <li>• <strong>{t('metronomeFeature3')}</strong></li>
                  <li>• <strong>{t('metronomeFeature4')}</strong></li>
                  <li>• <strong>{t('metronomeFeature5')}</strong></li>
                </ul>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                <div className="flex items-center mb-3">
                  <SafeIcon icon={FiUser} className="w-5 h-5 text-purple-500 dark:text-purple-400 mr-2" />
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200">{t('characterFeatures')}</h4>
                </div>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li>• <strong>{t('characterFeature1')}</strong></li>
                  <li>• <strong>{t('characterFeature2')}</strong></li>
                  <li>• <strong>{t('characterFeature3')}</strong></li>
                  <li>• <strong>{t('characterFeature4')}</strong></li>
                  <li>• <strong>{t('characterFeature5')}</strong></li>
                </ul>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                <div className="flex items-center mb-3">
                  <SafeIcon icon={FiAward} className="w-5 h-5 text-purple-500 dark:text-purple-400 mr-2" />
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200">{t('achievementFeatures')}</h4>
                </div>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li>• <strong>{t('achievementFeature1')}</strong></li>
                  <li>• <strong>{t('achievementFeature2')}</strong></li>
                  <li>• <strong>{t('achievementFeature3')}</strong></li>
                  <li>• <strong>{t('achievementFeature4')}</strong></li>
                  <li>• <strong>{t('achievementFeature5')}</strong></li>
                </ul>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                <div className="flex items-center mb-3">
                  <SafeIcon icon={FiSettings} className="w-5 h-5 text-purple-500 dark:text-purple-400 mr-2" />
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200">{t('appSettings')}</h4>
                </div>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li>• <strong>{t('settingsFeature1')}</strong></li>
                  <li>• <strong>{t('settingsFeature2')}</strong></li>
                  <li>• <strong>{t('settingsFeature3')}</strong></li>
                  <li>• <strong>{t('settingsFeature4')}</strong></li>
                  <li>• <strong>{t('settingsFeature5')}</strong></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <div className="flex items-center mb-6">
              <SafeIcon icon={FiInfo} className="w-6 h-6 text-purple-600 dark:text-purple-400 mr-2" />
              <h3 className="text-xl font-semibold text-purple-600 dark:text-purple-400">{t('contactSupport')}</h3>
            </div>
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl p-6">
              <p className="mb-4 text-gray-700 dark:text-gray-300">
                {t('supportText')}
              </p>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4">
                <p className="font-medium text-gray-800 dark:text-gray-200">{t('email')}：info@homy.hk</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HelpPage;